import LoginHandler from '@/pages/login/LoginHandler';
import LoginPage from '@/pages/login/LoginPage';
import ResetPassword from '@/pages/login/ResetPassword';
import { lazy, Suspense } from 'react';
import { Route } from 'react-router-dom';
import AppShellFocusFrame from '@/components/app-shell/AppShellFocusFrame';
import AppShellFrame from '@/components/app-shell/AppShellFrame';
import ShellFrameRoutes from '@/components/app-shell/ShellFrameRoutes';
import { useOffline } from '@/data/hook/useOffline';
import ChunkErrorBoundary from '@/errors/boundary/ChunkErrorBoundary';
import '@/lib/i18n';
import { UserTypeEnum } from '@/models/User';
import AdminPage from '@/pages/admin/AdminPage';
import CourseBox from '@/pages/admin/courses/box/CourseBox';
import { LessonBox } from '@/pages/admin/courses/box/LessonBox';
import CourseStudio from '@/pages/admin/courses/studio/CourseStudio';
import ManageHackathon from '@/pages/admin/manage/ManageHackathon';
import CalendarPage from '@/pages/calendar/CalendarPage';
import CourseLineFormPage from '@/pages/course-line/CourseLineAdminPage';
import CourseLineWorkspace from '@/pages/course-line/CourseLineWorkspace';
import CourseWorkspace from '@/pages/course-line/CourseWorkspace';
import ExamWorkspace from '@/pages/course-line/ExamWorkspace';
import LessonWorkspace from '@/pages/course-line/LessonWorkspace';
import ModuleWorkspace from '@/pages/course-line/ModuleWorkspace';
import UnitWorkspace from '@/pages/course-line/UnitWorkspace';
import CoursesPageSwitcher from '@/pages/courses/CoursesPageSwitcher';
import LoadingView from '@/pages/courses/LoadingView';
import Home from '@/pages/dashboard/DashboardPage';
import ErrorPage from '@/pages/notFound/ErrorPage';
import ProfilePage from '@/pages/ProfilePage';
import ProjectHackathonPage from '@/pages/project/hackathon/ProjectHackathonPage';
import ProjectHackathonRankingPage from '@/pages/project/hackathon/ProjectHackathonRankingPage';
import ProjectFormPage from '@/pages/project/ProjectFormPage';
import ProjectGeneralPage from '@/pages/project/ProjectGeneralPage';
import ProjectMePage from '@/pages/project/ProjectMePage';
import ProjectPage from '@/pages/project/ProjectPage';
import ProjectSectionPage from '@/pages/project/ProjectSectionPage';
import DeprecatedManageScheduledLessonReport from '@/pages/_deprecated/ManageScheduledLessonReport';
import { ROUTES } from '@/utils/routes';
import { FOCUS_PATHS, FOCUS_ROUTES } from '@/routes/focusRoutes';
import lessonRoutes from '@/pages/lesson/lessonRoutes';

const TermsOfUsePage = lazy(() => import('@/pages/TermsOfUsePage'));

const SkillPage = lazy(() => import('@/pages/skill/SkillPage'));

const EditMaterialPage = lazy(
  () =>
    import(
      '@/components/admin/lessons/material/EditMaterialPage/EditMaterialPage'
    ),
);
const AuthGuard = lazy(() => import('@/components/auth/AuthGuard'));
const HowItWorksView = lazy(
  () => import('@/components/parents/HowItWorksView'),
);
const AccountPage = lazy(() => import('@/pages/account/AccountPage'));
const KlassProgressView = lazy(() => import('@/pages/admin/KlassProgressView'));
const ManageActivityEdit = lazy(
  () => import('@/pages/admin/ManageActivityEdit'),
);
const ManageCourse = lazy(() => import('@/pages/admin/manage/ManageCourse'));
const ManageEditingCourse = lazy(
  () => import('@/pages/admin/ManageCourseEditing'),
);
const ManageKlass = lazy(() => import('@/pages/_deprecated/ManageKlasses'));
const KlassesPageSwitcher = lazy(
  () => import('@/pages/klasses/KlassesPageSwitcher'),
);
const KlassDetailPage = lazy(() => import('@/pages/klasses/KlassDetailPage'));
const ManageRecessCalendars = lazy(
  () => import('@/pages/admin/manage/ManageRecessCalendars'),
);
const ManageStudent = lazy(() => import('@/pages/_deprecated/ManageStudent'));
const StudentsPageSwitcher = lazy(
  () => import('@/pages/students/StudentsPageSwitcher'),
);
const StudentDetailPage = lazy(
  () => import('@/pages/students/StudentDetailPage'),
);
const ManageTeacher = lazy(() => import('@/pages/admin/manage/ManageTeacher'));
const ManageTeam = lazy(() => import('@/pages/admin/manage/ManageTeam'));
const ManageTrialLesson = lazy(
  () => import('@/pages/admin/manage/ManageTrialLesson'),
);
const ManageUnit = lazy(() => import('@/pages/admin/manage/ManageUnit'));
const SuggestionsPage = lazy(() => import('@/pages/admin/SuggestionsPage'));
const LessonsFeedbackPage = lazy(
  () => import('@/pages/feedback/LessonsFeedbackPage'),
);

const KlassView = lazy(() => import('@/pages/klass/KlassView'));
const LessonPageSwitcher = lazy(
  () => import('@/pages/lesson/LessonPageSwitcher'),
);
const KlassLessons = lazy(() => import('@/pages/klass/lessons/KlassLessons'));
const ManageExtraLessonsActivity = lazy(
  () => import('@/pages/klass/lessons/ManageExtraLessonsActivity'),
);

const ProjectView = lazy(() => import('@/pages/project/ProjectView'));

const StudentHistoryPage = lazy(
  () => import('@/pages/admin/StudentHistoryPage'),
);
const TopicsPage = lazy(() => import('@/pages/admin/manage/ManageTopics'));
const PromptsPage = lazy(() => import('@/pages/admin/manage/ManagePrompts'));
const NotificationsPage = lazy(() => import('@/pages/admin/NotificationsPage'));
const CourseLessonsPageSwitcher = lazy(
  () => import('@/pages/course-lessons/CourseLessonsPageSwitcher'),
);
const ExtraCoursesPortal = lazy(
  () => import('@/pages/courses/extra/ExtraCoursesPortal'),
);
const DeatchedLessonEditPage = lazy(
  () => import('@/pages/admin/DetachedLessonEditPage'),
);
const PendenciesPage = lazy(() => import('@/pages/pendencies/PendenciesPage'));
const ExamSchedulingPage = lazy(
  () => import('@/pages/exam-scheduling/ExamSchedulingPage'),
);
const ExamScheduleWizardPage = lazy(
  () => import('@/pages/exam-scheduling/ExamScheduleWizardPage'),
);
const CorrectionPage = lazy(() => import('@/pages/questions/CorrectionPage'));
const ManualCorrectionPage = lazy(
  () => import('@/pages/questions/ManualCorrectionPage'),
);
const ListActivitiesPendingCorrectionPage = lazy(
  () => import('@/pages/questions/ListActivitiesPendingCorrectionPage'),
);
const AICorrectionPage = lazy(
  () => import('@/pages/questions/AICorrectionPage'),
);
const ExamCorrectionPage = lazy(
  () => import('@/pages/exam-correction/ExamCorrectionPage'),
);
const ExamCorrectionSuccessPage = lazy(
  () => import('@/pages/exam-correction/ExamCorrectionSuccessPage'),
);

const Router = () => {
  const isOffline = useOffline();

  if (isOffline) {
    return <ErrorPage statusCode={1} />;
  }

  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<LoadingView />}>
        <ShellFrameRoutes focusPaths={FOCUS_PATHS}>
          <Route path="/terms-of-use" element={<TermsOfUsePage />} />
          <Route path="/login" element={<LoginPage />}>
            <Route index element={<LoginHandler />} />
            <Route
              path="reset-password/:uid/u/:token"
              element={<ResetPassword />}
            />
          </Route>
          <Route element={<AppShellFocusFrame />}>
            {FOCUS_ROUTES.map(({ path, element }) => (
              <Route key={path} path={path} element={element} />
            ))}
          </Route>
          <Route element={<AppShellFrame />}>
            <Route path="/" element={<Home />} />
            <Route path="/skills" element={<SkillPage />} />
            <Route path="/skills/:areaId" element={<SkillPage />} />
            <Route path="/projects" element={<ProjectPage />}>
              <Route index element={<ProjectGeneralPage />} />
              <Route path="section/" element={<ProjectSectionPage />} />
              <Route path="me/" element={<ProjectMePage />} />
              <Route
                path={ROUTES.HACKATHON.BASE}
                element={<ProjectHackathonPage />}
              />
              <Route
                path={ROUTES.HACKATHON.RANKING.BASE}
                element={<ProjectHackathonRankingPage />}
              />
            </Route>
            <Route
              path={ROUTES.PROJECTS.CREATE}
              element={<ProjectFormPage />}
            />
            <Route
              path={ROUTES.PROJECTS.EDIT.BASE}
              element={<ProjectFormPage />}
            />

            <Route path="/projects/:id" element={<ProjectView />} />
            <Route
              path={ROUTES.EXTRA_COURSES.BASE}
              element={
                <AuthGuard roles={[UserTypeEnum.STUDENT]}>
                  <ExtraCoursesPortal />
                </AuthGuard>
              }
            />
            <Route
              path="/teacher"
              element={
                <AuthGuard roles={[UserTypeEnum.TEACHER]}>
                  <AdminPage />
                </AuthGuard>
              }
            />
            <Route
              path={ROUTES.PENDENCIES.BASE}
              element={
                <AuthGuard
                  roles={[
                    UserTypeEnum.TEACHER,
                    UserTypeEnum.SUPER_ADMIN,
                    UserTypeEnum.UNIT_ADMIN,
                  ]}
                >
                  <PendenciesPage />
                </AuthGuard>
              }
            />
            <Route
              path={ROUTES.EXAM_SCHEDULING.BASE}
              element={
                <AuthGuard
                  roles={[
                    UserTypeEnum.TEACHER,
                    UserTypeEnum.SUPER_ADMIN,
                    UserTypeEnum.UNIT_ADMIN,
                  ]}
                >
                  <ExamSchedulingPage />
                </AuthGuard>
              }
            />
            <Route
              path={ROUTES.EXAM_SCHEDULING.SCHEDULE(':klassId', ':examId')}
              element={
                <AuthGuard
                  roles={[
                    UserTypeEnum.TEACHER,
                    UserTypeEnum.SUPER_ADMIN,
                    UserTypeEnum.UNIT_ADMIN,
                  ]}
                >
                  <ExamScheduleWizardPage />
                </AuthGuard>
              }
            />
            <Route
              path={ROUTES.EXAM_CORRECTION.PROGRESS(
                ':klassId',
                ':studentExamId',
              )}
              element={
                <AuthGuard
                  roles={[
                    UserTypeEnum.TEACHER,
                    UserTypeEnum.SUPER_ADMIN,
                    UserTypeEnum.UNIT_ADMIN,
                  ]}
                >
                  <ExamCorrectionPage />
                </AuthGuard>
              }
            />
            <Route
              path={ROUTES.EXAM_CORRECTION.CORRECTED(
                ':klassId',
                ':studentExamId',
              )}
              element={
                <AuthGuard
                  roles={[
                    UserTypeEnum.TEACHER,
                    UserTypeEnum.SUPER_ADMIN,
                    UserTypeEnum.UNIT_ADMIN,
                  ]}
                >
                  <ExamCorrectionSuccessPage />
                </AuthGuard>
              }
            />
            <Route
              path={ROUTES.ACTIVITIES.ACTIVITY_CORRECTION.ACTIVITY(
                ':klassId',
                ':activityProgressId',
              )}
              element={
                <AuthGuard
                  roles={[
                    UserTypeEnum.TEACHER,
                    UserTypeEnum.SUPER_ADMIN,
                    UserTypeEnum.UNIT_ADMIN,
                  ]}
                >
                  <CorrectionPage />
                </AuthGuard>
              }
            >
              <Route path="correction" element={<ManualCorrectionPage />} />
              <Route
                path="corrected"
                element={<ListActivitiesPendingCorrectionPage />}
              />
              <Route path="ai-correction" element={<AICorrectionPage />} />
            </Route>
            <Route
              path="/admin"
              element={
                <AuthGuard
                  roles={[UserTypeEnum.SUPER_ADMIN, UserTypeEnum.UNIT_ADMIN]}
                >
                  <AdminPage />
                </AuthGuard>
              }
            />
            <Route
              path="/admin/courses/:isAdd"
              element={
                <AuthGuard roles={[UserTypeEnum.SUPER_ADMIN]}>
                  <ManageCourse />
                </AuthGuard>
              }
            />
            <Route
              path={ROUTES.ADMIN.CLASSES.BASE}
              element={
                <AuthGuard
                  roles={[
                    UserTypeEnum.SUPER_ADMIN,
                    UserTypeEnum.UNIT_ADMIN,
                    UserTypeEnum.TEACHER,
                  ]}
                >
                  <KlassesPageSwitcher />
                </AuthGuard>
              }
            />
            <Route
              path={ROUTES.ADMIN.CLASSES.DETAILS(':klassId')}
              element={
                <AuthGuard
                  roles={[
                    UserTypeEnum.SUPER_ADMIN,
                    UserTypeEnum.UNIT_ADMIN,
                    UserTypeEnum.TEACHER,
                  ]}
                >
                  <KlassDetailPage />
                </AuthGuard>
              }
            />

            <Route
              path={ROUTES.ADMIN.COURSE_LINES.BASE}
              element={
                <AuthGuard
                  roles={[
                    UserTypeEnum.SUPER_ADMIN,
                    UserTypeEnum.CONTENT_CREATOR,
                  ]}
                >
                  <CourseLineFormPage />
                </AuthGuard>
              }
            >
              <Route index element={<CourseLineWorkspace />} />
              <Route path=":courseLineId" element={<CourseLineWorkspace />} />
              <Route
                path=":courseLineId/module/:moduleId"
                element={<ModuleWorkspace />}
              />
              <Route
                path=":courseLineId/module/:moduleId/ped-content/:slug"
                element={<CourseWorkspace />}
              />
              <Route
                path=":courseLineId/module/:moduleId/ped-content/:slug/unit/:unitId"
                element={<UnitWorkspace />}
              />
              <Route
                path=":courseLineId/module/:moduleId/ped-content/:slug/unit/:unitId/lesson/:lessonId"
                element={<LessonWorkspace />}
              />
              <Route
                path=":courseLineId/module/:moduleId/ped-content/:slug/exam/:examId"
                element={<ExamWorkspace />}
              />
            </Route>

            <Route
              path={ROUTES.ADMIN.CLASSES.ACTION(':actionId')}
              element={
                <AuthGuard
                  roles={[
                    UserTypeEnum.SUPER_ADMIN,
                    UserTypeEnum.UNIT_ADMIN,
                    UserTypeEnum.TEACHER,
                  ]}
                >
                  <ManageKlass />
                </AuthGuard>
              }
            />
            <Route
              path={ROUTES.CLASS.PROGRESS(':klassId')}
              element={
                <AuthGuard
                  roles={[
                    UserTypeEnum.SUPER_ADMIN,
                    UserTypeEnum.UNIT_ADMIN,
                    UserTypeEnum.TEACHER,
                  ]}
                >
                  <KlassProgressView />
                </AuthGuard>
              }
            />
            <Route
              path="/admin/student-history/:studentId"
              element={
                <AuthGuard
                  roles={[
                    UserTypeEnum.SUPER_ADMIN,
                    UserTypeEnum.UNIT_ADMIN,
                    UserTypeEnum.TEACHER,
                  ]}
                >
                  <StudentHistoryPage />
                </AuthGuard>
              }
            />

            <Route
              path="/admin/students/"
              element={
                <AuthGuard
                  roles={[
                    UserTypeEnum.SUPER_ADMIN,
                    UserTypeEnum.UNIT_ADMIN,
                    UserTypeEnum.TEACHER,
                  ]}
                >
                  <StudentsPageSwitcher />
                </AuthGuard>
              }
            />

            <Route
              path={ROUTES.ADMIN.STUDENTS.DETAILS(':studentId')}
              element={
                <AuthGuard
                  roles={[
                    UserTypeEnum.SUPER_ADMIN,
                    UserTypeEnum.UNIT_ADMIN,
                    UserTypeEnum.TEACHER,
                  ]}
                >
                  <StudentDetailPage />
                </AuthGuard>
              }
            />

            <Route
              path={ROUTES.ADMIN.STUDENTS.ACTION(':actionId')}
              element={
                <AuthGuard
                  roles={[
                    UserTypeEnum.SUPER_ADMIN,
                    UserTypeEnum.UNIT_ADMIN,
                    UserTypeEnum.TEACHER,
                  ]}
                >
                  <ManageStudent />
                </AuthGuard>
              }
            />

            <Route
              path="/admin/teachers/"
              element={
                <AuthGuard
                  roles={[UserTypeEnum.SUPER_ADMIN, UserTypeEnum.UNIT_ADMIN]}
                >
                  <ManageTeacher />
                </AuthGuard>
              }
            />
            <Route
              path={ROUTES.ADMIN.TEACHERS.ACTION(':actionId')}
              element={
                <AuthGuard
                  roles={[UserTypeEnum.SUPER_ADMIN, UserTypeEnum.UNIT_ADMIN]}
                >
                  <ManageTeacher />
                </AuthGuard>
              }
            />

            <Route
              path="/admin/team/t/:username"
              element={
                <AuthGuard
                  roles={[UserTypeEnum.SUPER_ADMIN, UserTypeEnum.UNIT_ADMIN]}
                >
                  <ManageTeam />
                </AuthGuard>
              }
            />
            <Route
              path="/admin/trial-lesson"
              element={
                <AuthGuard
                  roles={[UserTypeEnum.SUPER_ADMIN, UserTypeEnum.UNIT_ADMIN]}
                >
                  <ManageTrialLesson />
                </AuthGuard>
              }
            />
            <Route
              path="/admin/team/"
              element={
                <AuthGuard
                  roles={[UserTypeEnum.SUPER_ADMIN, UserTypeEnum.UNIT_ADMIN]}
                >
                  <ManageTeam />
                </AuthGuard>
              }
            />
            <Route
              path={ROUTES.ADMIN.TEAM.ACTION(':actionId')}
              element={
                <AuthGuard
                  roles={[UserTypeEnum.SUPER_ADMIN, UserTypeEnum.UNIT_ADMIN]}
                >
                  <ManageTeam />
                </AuthGuard>
              }
            />
            <Route
              path="/admin/recesscalendars"
              element={
                <AuthGuard roles={[UserTypeEnum.UNIT_ADMIN]}>
                  <ManageRecessCalendars />
                </AuthGuard>
              }
            />
            <Route
              path="/admin/topics"
              element={
                <AuthGuard roles={[UserTypeEnum.SUPER_ADMIN]}>
                  <TopicsPage />
                </AuthGuard>
              }
            />
            <Route
              path="/admin/prompts"
              element={
                <AuthGuard roles={[UserTypeEnum.SUPER_ADMIN]}>
                  <PromptsPage />
                </AuthGuard>
              }
            />
            <Route
              path={ROUTES.NOTIFICATIONS.BASE}
              element={
                <AuthGuard
                  roles={[
                    UserTypeEnum.SUPER_ADMIN,
                    UserTypeEnum.UNIT_ADMIN,
                    UserTypeEnum.TEACHER,
                  ]}
                >
                  <NotificationsPage />
                </AuthGuard>
              }
            />
            <Route
              path="/como-funciona"
              element={
                <AuthGuard roles={[UserTypeEnum.PARENT]}>
                  <HowItWorksView />
                </AuthGuard>
              }
            />

            <Route path="/courses">
              <Route index element={<CoursesPageSwitcher />} />

              <Route
                path="/courses/:slugCourseName"
                element={
                  <AuthGuard
                    roles={[
                      UserTypeEnum.SUPER_ADMIN,
                      UserTypeEnum.UNIT_ADMIN,
                      UserTypeEnum.TEACHER,
                      UserTypeEnum.CONTENT_CREATOR,
                      UserTypeEnum.STUDENT,
                    ]}
                  >
                    <CourseLessonsPageSwitcher />
                  </AuthGuard>
                }
              />
              <Route
                path="/courses/:slugCourseName/class/:klassId"
                element={
                  <AuthGuard
                    roles={[
                      UserTypeEnum.SUPER_ADMIN,
                      UserTypeEnum.UNIT_ADMIN,
                      UserTypeEnum.TEACHER,
                      UserTypeEnum.STUDENT,
                    ]}
                  >
                    <CourseLessonsPageSwitcher />
                  </AuthGuard>
                }
              />
            </Route>
            <Route path="/forbidden" element={<ErrorPage statusCode={403} />} />
            <Route path="/profile/:username" element={<ProfilePage />} />

            <Route
              path={ROUTES.LESSONS_FEEDBACK.BASE}
              element={
                <AuthGuard
                  roles={[
                    UserTypeEnum.TEACHER,
                    UserTypeEnum.SUPER_ADMIN,
                    UserTypeEnum.UNIT_ADMIN,
                  ]}
                >
                  <LessonsFeedbackPage />
                </AuthGuard>
              }
            />

            <Route
              path="/admin/calendars"
              element={
                <AuthGuard
                  roles={[UserTypeEnum.SUPER_ADMIN, UserTypeEnum.UNIT_ADMIN]}
                >
                  <ManageRecessCalendars />
                </AuthGuard>
              }
            />
            <Route
              path="/admin/units/:unitId/recess-calendar"
              element={
                <AuthGuard
                  roles={[UserTypeEnum.SUPER_ADMIN, UserTypeEnum.UNIT_ADMIN]}
                >
                  <ManageRecessCalendars />
                </AuthGuard>
              }
            />
            <Route
              path="/courses/box/:slug"
              element={
                <AuthGuard roles={[UserTypeEnum.SUPER_ADMIN]}>
                  <CourseBox />
                </AuthGuard>
              }
            >
              <Route index element={<LessonBox />} />
              <Route
                path="/courses/box/:slug/lesson/:lessonOrder"
                element={<LessonBox />}
              />
            </Route>
            <Route
              path={ROUTES.COURSES.STUDIO.BASE}
              element={
                <AuthGuard
                  roles={[
                    UserTypeEnum.SUPER_ADMIN,
                    UserTypeEnum.CONTENT_CREATOR,
                  ]}
                >
                  <CourseStudio />
                </AuthGuard>
              }
            >
              <Route index element={<CourseStudio />} />
              <Route
                path={ROUTES.COURSES.STUDIO.INFO(':slug')}
                element={
                  <AuthGuard
                    roles={[
                      UserTypeEnum.SUPER_ADMIN,
                      UserTypeEnum.CONTENT_CREATOR,
                    ]}
                  >
                    <CourseStudio />
                  </AuthGuard>
                }
              >
                <Route
                  path=":studioStep"
                  element={
                    <AuthGuard
                      roles={[
                        UserTypeEnum.SUPER_ADMIN,
                        UserTypeEnum.CONTENT_CREATOR,
                      ]}
                    >
                      <CourseStudio />
                    </AuthGuard>
                  }
                />
              </Route>
            </Route>
            <Route
              path="/admin/courses/"
              element={
                <AuthGuard roles={[UserTypeEnum.SUPER_ADMIN]}>
                  <ManageCourse />
                </AuthGuard>
              }
            />
            <Route
              path={ROUTES.ADMIN.HACKATHONS.BASE}
              element={
                <AuthGuard roles={[UserTypeEnum.SUPER_ADMIN]}>
                  <ManageHackathon />
                </AuthGuard>
              }
            />
            <Route
              path="/admin/courses/:slugCourseName/lessons/editing"
              element={
                <AuthGuard
                  roles={[
                    UserTypeEnum.SUPER_ADMIN,
                    UserTypeEnum.CONTENT_CREATOR,
                  ]}
                >
                  <ManageEditingCourse page="edit" />
                </AuthGuard>
              }
            />
            <Route
              path="/admin/courses/:slugCourseName/lessons/versioning"
              element={
                <AuthGuard
                  roles={[
                    UserTypeEnum.SUPER_ADMIN,
                    UserTypeEnum.CONTENT_CREATOR,
                  ]}
                >
                  <ManageEditingCourse page="version" />
                </AuthGuard>
              }
            />
            <Route
              path="/admin/courses/:slugCourseName/lessons/draft"
              element={
                <AuthGuard
                  roles={[
                    UserTypeEnum.SUPER_ADMIN,
                    UserTypeEnum.CONTENT_CREATOR,
                  ]}
                >
                  <ManageEditingCourse page="draft" />
                </AuthGuard>
              }
            />
            <Route
              path={
                '/admin/courses/:slugCourseName/lessons/:lessonId/:bookType/:bookId'
              }
              element={
                <AuthGuard
                  roles={[
                    UserTypeEnum.SUPER_ADMIN,
                    UserTypeEnum.CONTENT_CREATOR,
                  ]}
                >
                  <EditMaterialPage />
                </AuthGuard>
              }
            >
              <Route
                path={
                  '/admin/courses/:slugCourseName/lessons/:lessonId/:bookType/:bookId/chapter/:chapterId'
                }
                element={
                  <AuthGuard
                    roles={[
                      UserTypeEnum.SUPER_ADMIN,
                      UserTypeEnum.CONTENT_CREATOR,
                    ]}
                  >
                    <EditMaterialPage />
                  </AuthGuard>
                }
              />
            </Route>
            <Route
              path={
                '/admin/courses/:slugCourseName/lesson-blocks/:blockId/lessons/:lessonId/:bookType/:bookId'
              }
              element={
                <AuthGuard
                  roles={[
                    UserTypeEnum.SUPER_ADMIN,
                    UserTypeEnum.CONTENT_CREATOR,
                  ]}
                >
                  <EditMaterialPage />
                </AuthGuard>
              }
            >
              <Route
                path={
                  '/admin/courses/:slugCourseName/lesson-blocks/:blockId/lessons/:lessonId/:bookType/:bookId/chapter/:chapterId'
                }
                element={
                  <AuthGuard
                    roles={[
                      UserTypeEnum.SUPER_ADMIN,
                      UserTypeEnum.CONTENT_CREATOR,
                    ]}
                  >
                    <EditMaterialPage />
                  </AuthGuard>
                }
              />
            </Route>
            {lessonRoutes({
              basePath:
                '/courses/:slugCourseName/lesson/:lessonId/class/:klassId',
              lessonElement: <LessonPageSwitcher />,
              legacyElement: <KlassView />,
            })}
            {lessonRoutes({
              basePath: '/courses/:slugCourseName/lesson/:lessonId',
              lessonElement: <LessonPageSwitcher />,
              legacyElement: <KlassView />,
            })}
            <Route path={ROUTES.LESSONS.DETACHED.BASE}>
              <Route
                index
                element={
                  <AuthGuard
                    roles={[
                      UserTypeEnum.SUPER_ADMIN,
                      UserTypeEnum.UNIT_ADMIN,
                      UserTypeEnum.CONTENT_CREATOR,
                    ]}
                  >
                    <DeatchedLessonEditPage />
                  </AuthGuard>
                }
              />
              <Route
                path={ROUTES.LESSONS.DETACHED.NESTED_MATERIAL}
                element={
                  <AuthGuard
                    roles={[
                      UserTypeEnum.SUPER_ADMIN,
                      UserTypeEnum.CONTENT_CREATOR,
                    ]}
                  >
                    <EditMaterialPage />
                  </AuthGuard>
                }
              />
              <Route
                path={ROUTES.LESSONS.DETACHED.NESTED_CHAPTER}
                element={
                  <AuthGuard
                    roles={[
                      UserTypeEnum.SUPER_ADMIN,
                      UserTypeEnum.CONTENT_CREATOR,
                    ]}
                  >
                    <EditMaterialPage />
                  </AuthGuard>
                }
              />
              <Route
                path={ROUTES.LESSONS.DETACHED.NESTED_ACTIVITY}
                element={
                  <AuthGuard
                    roles={[
                      UserTypeEnum.SUPER_ADMIN,
                      UserTypeEnum.UNIT_ADMIN,
                      UserTypeEnum.CONTENT_CREATOR,
                    ]}
                  >
                    <ManageActivityEdit />
                  </AuthGuard>
                }
              />
            </Route>
            <Route
              path="/admin/courses/:slugCourseName/lessons/:lessonId/activities/:activityId"
              element={
                <AuthGuard
                  roles={[
                    UserTypeEnum.SUPER_ADMIN,
                    UserTypeEnum.UNIT_ADMIN,
                    UserTypeEnum.CONTENT_CREATOR,
                  ]}
                >
                  <ManageActivityEdit />
                </AuthGuard>
              }
            />
            <Route path="/admin/units">
              <Route
                index
                element={
                  <AuthGuard roles={[UserTypeEnum.SUPER_ADMIN]}>
                    <ManageUnit />
                  </AuthGuard>
                }
              />
              <Route
                path=":isAdd"
                element={
                  <AuthGuard roles={[UserTypeEnum.SUPER_ADMIN]}>
                    <ManageUnit />
                  </AuthGuard>
                }
              />
            </Route>
            <Route
              path="/calendar"
              element={
                <AuthGuard
                  roles={[
                    UserTypeEnum.SUPER_ADMIN,
                    UserTypeEnum.TEACHER,
                    UserTypeEnum.UNIT_ADMIN,
                    UserTypeEnum.PARENT,
                  ]}
                >
                  <CalendarPage />
                </AuthGuard>
              }
            />
            <Route path={ROUTES.ACCOUNT.BASE} element={<AccountPage />} />
            <Route path="/sugestoes" element={<SuggestionsPage />} />
            <Route
              path="/lesson-report"
              element={
                <AuthGuard roles={[UserTypeEnum.TEACHER]}>
                  <DeprecatedManageScheduledLessonReport />
                </AuthGuard>
              }
            />
            <Route
              path="/class/:klassId/lesson-report/:scheduledLessonId"
              element={
                <AuthGuard
                  roles={[
                    UserTypeEnum.TEACHER,
                    UserTypeEnum.SUPER_ADMIN,
                    UserTypeEnum.UNIT_ADMIN,
                  ]}
                >
                  <DeprecatedManageScheduledLessonReport />
                </AuthGuard>
              }
            />
            <Route path="/class/:klassId/lessons">
              <Route
                index
                element={
                  <AuthGuard
                    roles={[
                      UserTypeEnum.TEACHER,
                      UserTypeEnum.SUPER_ADMIN,
                      UserTypeEnum.UNIT_ADMIN,
                    ]}
                  >
                    <KlassLessons />
                  </AuthGuard>
                }
              />
              <Route
                path=":lessonId/activities/:activityId"
                element={
                  <AuthGuard
                    roles={[
                      UserTypeEnum.SUPER_ADMIN,
                      UserTypeEnum.UNIT_ADMIN,
                      UserTypeEnum.TEACHER,
                    ]}
                  >
                    <ManageExtraLessonsActivity />
                  </AuthGuard>
                }
              />
            </Route>
            <Route path="*" element={<ErrorPage statusCode={404} />} />
          </Route>
        </ShellFrameRoutes>
      </Suspense>
    </ChunkErrorBoundary>
  );
};

export default Router;
